import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { Opcion } from '../../../models/catalogo.type'
import { AppConstantes } from '../../../utils/Constantes'
import { TRecordatorioForm } from '../../recordatorios/models/recordatorioForm.type'
import { TTareaCondicionanteForm } from './tareaCondicionanteForm.type'

export type TCondicionanteForm = {
  idSitio?: Opcion | string | null
  idCondicionante?: Opcion | string | null
  idAutorizacion?: Opcion | string | null
  nombre: string
  descripcion: string
  estatus: Opcion
  periodicidad: Opcion
  cantidadPeriodos?: number
  tareas: TTareaCondicionanteForm[] | []
  recordatoriosRecurrentes?: TRecordatorioForm[]
}

export const style = {
  borderRadius: '10px',
  background: '#333',
  color: '#fff',
  fontSize: '18px',
}

/**
 * Valida la captura de condicionantes junto con sus tareas y recordatorios.
 * Las reglas anidadas evitan crear tareas sin responsable, fechas o estatus, y
 * aplican la misma condicion de recordatorio personalizado usada en otros flujos.
 */
export const schema = yup.object({
  nombre: yup.string().trim().required('El nombre es requerido'),
  descripcion: yup.string().trim().required('La descripción es requerida'),
  estatus: yup.object().nullable().required('El estatus es requerido'),
  cantidadPeriodos: yup.number(),
  periodicidad: yup.object().nullable().required('El periodo es requerido'),
  tareas: yup.array().of(
    yup.object().shape({
      titulo: yup.string().trim().required('El título es requerido'),
      descripcion: yup.string().trim().required('La descripción es requerida'),
      idUsuarioAsignado: yup.object().nullable().required('El usuario es requerido'),
      estatus: yup.object().nullable().required('El estatus es requerido'),
      fechaInicio: yup.string().nullable().required('La fecha es requerida'),
      fechaTermino: yup.string().nullable().required('La fecha es requerida'),
      recordatorios: yup.array().of(
        yup.object().shape({
          unidad: yup.object().nullable().required('La unidad del periodo de tiempo es requerida'),
          cantidad: yup
            .number()
            .transform((value) => (isNaN(value) ? undefined : value))
            .nullable()
            .when(['unidad'], {
              is: (unidad: { etiqueta: string; valor: string }) => {
                return (
                  unidad && unidad.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
                )
              },
              then: yup
                .number()
                .required('Cantidad del periodo de tiempo es requerida')
                .typeError('La cantidad del periodo de tiempo requiere de un número')
                .min(1, 'La cantidad debe ser mayor a 0')
                .max(9999, 'Máximo 4 dígitos'),
            }),
          fechaRecordatorio: yup
            .string()
            .nullable()
            .when(['unidad'], {
              is: (unidad: { etiqueta: string; valor: string }) => {
                return (
                  unidad && unidad.valor === AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
                )
              },
              then: yup.string().nullable().default(null).required('Una fecha es requerida'),
            }),
        })
      ),
    })
  ),
  recordatoriosRecurrentes: yup.array().of(
    yup.object().shape({
      unidad: yup.object().nullable().required('La unidad del periodo de tiempo es requerida'),
      cantidad: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .nullable()
        .when(['unidad'], {
          is: (unidad: Opcion) => {
            return unidad && unidad.valor !== AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
          },
          then: yup
            .number()
            .required('Cantidad del periodo de tiempo es requerida')
            .typeError('La cantidad del periodo de tiempo requiere de un número')
            .min(1, 'La cantidad debe ser mayor a 0')
            .max(9999, 'Máximo 4 dígitos'),
        }),
      fechaRecordatorio: yup
        .string()
        .nullable()
        .when(['unidad'], {
          is: (unidad: Opcion) => {
            return unidad && unidad.valor === AppConstantes.CVE_UNIDAD_RECORDATORIO_PERSONALIZADO
          },
          then: yup.string().nullable().default(null).required('Una fecha es requerida'),
        }),
    })
  ),
})

export const useCondicionanteForm = () => {
  return useForm<TCondicionanteForm>({
    mode: 'all',
    resolver: yupResolver(schema),
  })
}
